/**
 * @module Components/FeatureComponent/Sidebar
 *
 * @description The `Sidebar` component is a vertical navigation menu used in the user profile section. It displays different tabs for managing
 * user profile details, including personal details, address, bank details, purchase history, and more. The sidebar also includes
 * footer links for logging out, viewing the privacy policy, and deleting an account (if applicable).
 *
 * The sidebar is responsive and adjusts the styles and available tabs based on the user's role (`partner` role) and whether the demo is active.
 */

import { Box, Divider, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { ContextProvider } from 'App';
import { useContext } from 'react';
import DeleteAccountSection from './DeleteAccountSection';
import LogoutSection from './LogoutSection';

/**
 * Accessibility properties for the tabs, providing unique ID and aria-controls for each tab.
 *
 * @function
 * @param {number} index - The index of the tab to generate the accessibility properties.
 * @returns {Object} - An object containing accessibility attributes `id` and `aria-controls` for the tab.
 */
function a11yProps(index) {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  };
}

/**
 * Sidebar component for displaying navigation tabs and footer links in the user profile section.
 *
 * The component includes tabs for various user profile settings, such as personal details, address, and account information.
 * It also conditionally displays a logout link, privacy policy, and the option to delete the account based on the user's role and session state.
 *
 * @component
 *
 * @param {Object} props - The component props.
 * @param {number} props.value - The currently selected tab index.
 * @param {Function} props.handleTabChange - The function to handle tab changes.
 *
 * @returns {JSX.Element} The Sidebar component containing navigation tabs and footer links.
 */
const Sidebar = ({ value, handleTabChange }) => {
  const theme = useTheme();
  const { user } = useContext(ContextProvider); // Accessing the user context and checking the user's role and demo status
  const isPartnerUser = user?.role === 'partner';
  const isDemoActive = sessionStorage.getItem('isDemoActive') === 'true';

  /**
   * Opens the privacy policy page in a new tab.
   *
   * @function
   */
  const handlePrivacyPolicyClick = () => {
    window.open('https://getwilldone.com/privacy-policy/', 'Get Will Done');
  };

  /**
   * Calculates the style for the tabs based on the selected tab index.
   *
   * @function
   * @param {number} index - The index of the tab to calculate styles for.
   * @returns {Object} - The styles for the tab.
   */
  const tabStyle = (index) => ({
    width: value === index ? '182px' : 'auto',
    height: value === index ? '40px' : 'auto',
    borderRadius: value === index ? '30px' : '0px',
    border: value === index ? `1px solid ${theme.palette.primary.light}` : 'none',
    backgroundColor: value === index ? theme.palette.primary.lighter : 'transparent',
    opacity: value === index ? 1 : 'inherit',
    mb: 1,
    mt: index === 0 ? '2.310rem' : 'none',
  });

  /**
   * Renders a single tab with the provided label and index.
   *
   * @function
   * @param {string} label - The label to display on the tab.
   * @param {number} index - The index of the tab.
   * @returns {JSX.Element} - A Tab component with custom styles.
   */
  const renderTab = (label, index) => (
    <Tab label={<Typography variant="bodyText3">{label}</Typography>} {...a11yProps(index)} sx={tabStyle(index)} />
  );

  return (
    <>
      <Box
        sx={{
          width: '250px',
          height: '80vh',
          paddingLeft: '5px',
          marginTop: '9px',
          display: 'flex',
          flexDirection: 'row', // Change to row to place tabs and divider side by side
          justifyContent: 'flex-start', // Align items to the start
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between', // Ensures footer stays at the bottom
            flexGrow: 1, // Allow this Box to take up the remaining space
          }}
        >
          <Box>
            <Typography variant="title1">Profile</Typography>
            <Tabs
              orientation="vertical"
              value={value}
              onChange={handleTabChange}
              aria-label="Vertical tabs example"
              sx={{
                // borderRight: 1,
                // borderColor: theme.palette.grey[100],
                height: '100%', // Adjust height to account for footer
                '& .MuiTabs-indicator': {
                  display: 'none', // Hide the indicator for the selected tab
                },
                '& .MuiTab-root': {
                  textTransform: 'none', // Remove default text transformation
                  alignItems: 'flex-start',
                },
              }}
            >
              {renderTab('Personal Details', 0)}
              {renderTab('Address', 1)}
              {isPartnerUser && !isDemoActive && renderTab('Bank Details', 2)}
              {renderTab('Existing Will', isPartnerUser ? (isDemoActive ? 2 : 3) : 2)}
              {renderTab('Purchase History', isPartnerUser ? (isDemoActive ? 3 : 4) : 3)}
              {renderTab('Version History', isPartnerUser ? (isDemoActive ? 4 : 5) : 4)}
            </Tabs>
          </Box>

          {/* Footer Links */}
          <Box
            sx={{
              pt: 0.5, // Slight top padding to maintain some spacing while raising the links
              pb: 0, // No bottom padding
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            <LogoutSection />
            <Typography
              variant="suggestiveText"
              onClick={handlePrivacyPolicyClick}
              sx={{
                color: theme.palette.grey[300],
                textDecoration: 'none',
                mb: 1,
                cursor: 'default',
                '&:hover': {
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  color: theme.palette.grey[400],
                },
              }}
            >
              Privacy Policy
            </Typography>
            {!isPartnerUser && <DeleteAccountSection />}
          </Box>
        </Box>
        <Divider
          orientation="vertical"
          sx={{ height: '93%', width: '1px', mx: '20px', color: '#C5C5C5', mt: 8 }} // Ensure it spans the full height of the container
        />
      </Box>
    </>
  );
};

export default Sidebar;
